import { z } from 'zod';
import { GeocodeAccuracy } from './devices.js';
/**
 * Information exchange (sub-capability 4.2). Every interface with AEMO, the
 * STC registry and the network operator is a published, versioned contract
 * defined here; the interface catalogue (docs/interface-catalogue.md) is
 * generated from `interfaces` below, so the document and the code cannot
 * drift apart.
 */
const IsoDate = z.string().date('valid YYYY-MM-DD date required');
const IsoDateTime = z.string().datetime({ offset: true });
export const Partner = z.enum(['aemo', 'stc', 'network']);
export const Transport = z.enum(['rest', 'event', 'file']);
export const SchemaVersion = z.enum(['v1', 'v2']);
// ---- Envelope ----------------------------------------------------------------
/**
 * Common envelope for every message in either direction. `messageId` identifies
 * one transmission (a replay re-sends the same id); `idempotencyKey` identifies
 * the business fact (two transmissions with different ids but the same key are
 * duplicates); `effectiveDate` is when the fact became true, which the
 * bitemporal register uses as `valid_from` for late arrivals.
 */
export const Envelope = z.object({
    messageId: z.string().uuid(),
    idempotencyKey: z.string().min(1).max(200),
    schemaVersion: SchemaVersion,
    eventType: z.string().min(1),
    producerTimestamp: IsoDateTime,
    effectiveDate: IsoDate,
    correlationId: z.string().uuid().optional(),
    payload: z.unknown(),
});
// ---- Outbound (RegiGov -> partner) --------------------------------------------
const Address = z.object({ line1: z.string(), suburb: z.string(), state: z.string(), postcode: z.string() }).passthrough();
/** The installation's geocoded point. Omitted when RegiGov has none; not compared in reconciliation, since partners don't send it back. */
const PartnerLocation = z.object({ lat: z.number(), lon: z.number(), accuracy: GeocodeAccuracy });
/** AEMO device record v1: the unit and, when installed, its connection point. */
export const AemoDeviceRecordV1 = z.object({
    deviceId: z.string(),
    serial: z.string(),
    modelId: z.string(),
    modelNumber: z.string(),
    deviceClass: z.string(),
    status: z.enum(['registered', 'installed', 'decommissioned', 'withdrawn']),
    ownerParticipantId: z.string(),
    installation: z
        .object({
        installationId: z.string(),
        nmi: z.string(),
        installerParticipantId: z.string(),
        commissionedOn: IsoDate,
        decommissionedOn: IsoDate.nullable(),
        address: Address,
        location: PartnerLocation.optional(),
    })
        .nullable(),
});
/**
 * AEMO device record v2 coexists with v1: it adds the batch, the effective
 * date and rule version applied, and structured capability attributes. v1 stays
 * published so a consumer on v1 keeps receiving the v1 shape.
 */
export const AemoDeviceRecordV2 = AemoDeviceRecordV1.extend({
    batch: z.string().nullable(),
    effectiveDate: IsoDate,
    ruleVersionApplied: z.number().int(),
    attributes: z.record(z.unknown()),
});
/** STC registry v1: approved models and the units registered against them. */
export const StcModelRecordV1 = z.object({
    modelId: z.string(),
    manufacturerId: z.string(),
    modelNumber: z.string(),
    name: z.string(),
    deviceClass: z.string(),
    status: z.enum(['pending', 'approved', 'withdrawn']),
});
export const StcUnitRecordV1 = z.object({
    deviceId: z.string(),
    serial: z.string(),
    modelId: z.string(),
    ownerParticipantId: z.string(),
    status: z.enum(['registered', 'installed', 'decommissioned', 'withdrawn']),
    installedNmi: z.string().nullable(),
});
/** Network operator v1: installations at connection points on its network. */
export const NetworkInstallationRecordV1 = z.object({
    installationId: z.string(),
    nmi: z.string(),
    deviceId: z.string(),
    serial: z.string(),
    modelNumber: z.string(),
    deviceClass: z.string(),
    installerParticipantId: z.string(),
    status: z.enum(['active', 'decommissioned']),
    commissionedOn: IsoDate,
    decommissionedOn: IsoDate.nullable(),
    address: Address,
    location: PartnerLocation.optional(),
});
// ---- Inbound (partner -> RegiGov) ----------------------------------------------
/** AEMO tells us about a connection point's DER registration status (v1) ... */
export const AemoConnectionUpdatedV1 = z.object({
    installationId: z.string(),
    nmi: z.string(),
    derId: z.string(),
    connectionStatus: z.enum(['pending', 'connected', 'disconnected']),
});
/** ... and in v2 also the export limit it has applied. */
export const AemoConnectionUpdatedV2 = AemoConnectionUpdatedV1.extend({
    exportLimitKw: z.number().nonnegative().nullable(),
});
/** STC tells us a certificate claim was recorded against a unit (REST and CSV rows share this shape). */
export const StcClaimRecordedV1 = z.object({
    deviceId: z.string(),
    serial: z.string(),
    claimRef: z.string(),
    certificates: z.number().int().nonnegative(),
    claimedOn: IsoDate,
});
/** Column order of the STC CSV drop; the header row must match exactly. */
export const STC_CSV_COLUMNS = ['messageId', 'idempotencyKey', 'producerTimestamp', 'effectiveDate', 'deviceId', 'serial', 'claimRef', 'certificates', 'claimedOn'];
/** The network operator confirms an installation is commissioned on its network and the export limit set. */
export const NetworkCommissioningConfirmedV1 = z.object({
    installationId: z.string(),
    nmi: z.string(),
    networkReference: z.string(),
    exportLimitKw: z.number().nonnegative().nullable(),
    commissioningConfirmed: z.boolean(),
});
const outboundRetry = 'Worker retries with exponential backoff (5 attempts: 30s, 2m, 8m, 30m, 2h); the delivery log records every attempt.';
const outboundDlq = 'After the final attempt the message is dead-lettered; an operator replays it from the Information exchange workbench.';
const inboundDlq = 'Malformed or unapplicable messages are dead-lettered with the validation errors; an operator replays (optionally with a corrected payload) or discards them.';
const inboundRetry = 'Producer retries are safe: duplicates and replays are detected and recorded without changing the register.';
export const interfaces = [
    {
        id: 'aemo.device-record',
        partner: 'aemo',
        direction: 'outbound',
        transport: 'rest',
        version: 'v1',
        status: 'supported',
        eventTypes: ['unit.registered', 'unit.updated', 'unit.corrected', 'installation.recorded', 'installation.updated', 'installation.corrected'],
        entities: ['unit', 'installation'],
        endpoint: 'POST {AEMO}/aemo/v1/devices',
        auth: 'Bearer partner API key (sandbox: static key per partner)',
        idempotency: 'Idempotency-Key header = `<entityId>:<version>`; AEMO ignores repeats.',
        retry: outboundRetry,
        deadLetter: outboundDlq,
        reconciliation: 'Nightly and on-demand: GET {AEMO}/aemo/v1/snapshot compared with current units; differences queued for CER disposition. The optional installation `location` is not compared.',
        schema: AemoDeviceRecordV1,
        description: 'Current state of a device unit and its connection point after every register change. `installation.location` (lat, lon, accuracy) is present when RegiGov has geocoded the installation.',
    },
    {
        id: 'aemo.device-record',
        partner: 'aemo',
        direction: 'outbound',
        transport: 'rest',
        version: 'v2',
        status: 'current',
        eventTypes: ['unit.registered', 'unit.updated', 'unit.corrected', 'installation.recorded', 'installation.updated', 'installation.corrected'],
        entities: ['unit', 'installation'],
        endpoint: 'POST {AEMO}/aemo/v2/devices',
        auth: 'Bearer partner API key',
        idempotency: 'Idempotency-Key header = `<entityId>:<version>`',
        retry: outboundRetry,
        deadLetter: outboundDlq,
        reconciliation: 'As v1; snapshot compared on the v2 fields.',
        schema: AemoDeviceRecordV2,
        description: 'v1 plus batch, effective date, rule version and capability attributes. Published alongside v1 to demonstrate version coexistence.',
    },
    {
        id: 'aemo.connection-updated',
        partner: 'aemo',
        direction: 'inbound',
        transport: 'event',
        version: 'v1',
        status: 'supported',
        eventTypes: ['der.connection.updated'],
        entities: ['installation'],
        endpoint: 'POST {REGIGOV}/exchange/inbox/aemo/v1/events',
        auth: 'X-Partner-Key header (sandbox static key)',
        idempotency: 'Envelope idempotencyKey; same messageId = replay, same key with a new messageId = duplicate.',
        retry: inboundRetry,
        deadLetter: inboundDlq,
        reconciliation: 'Connection status is a partner fact on the installation and is compared during the AEMO reconciliation run.',
        schema: AemoConnectionUpdatedV1,
        description: 'Webhook: AEMO reports the DER registration status of a connection point.',
    },
    {
        id: 'aemo.connection-updated',
        partner: 'aemo',
        direction: 'inbound',
        transport: 'event',
        version: 'v2',
        status: 'current',
        eventTypes: ['der.connection.updated'],
        entities: ['installation'],
        endpoint: 'POST {REGIGOV}/exchange/inbox/aemo/v2/events',
        auth: 'X-Partner-Key header',
        idempotency: 'As v1.',
        retry: inboundRetry,
        deadLetter: inboundDlq,
        reconciliation: 'As v1.',
        schema: AemoConnectionUpdatedV2,
        description: 'v1 plus the export limit AEMO applied. Both versions are accepted concurrently.',
    },
    {
        id: 'stc.model-record',
        partner: 'stc',
        direction: 'outbound',
        transport: 'rest',
        version: 'v1',
        status: 'current',
        eventTypes: ['model.registered', 'model.updated', 'model.corrected'],
        entities: ['model'],
        endpoint: 'POST {STC}/stc/v1/models',
        auth: 'Bearer partner API key',
        idempotency: 'Idempotency-Key header = `<entityId>:<version>`',
        retry: outboundRetry,
        deadLetter: outboundDlq,
        reconciliation: 'GET {STC}/stc/v1/snapshot?interface=stc.model-record compared with current models (its own run).',
        schema: StcModelRecordV1,
        description: 'Approved-model list the STC registry validates certificate claims against.',
    },
    {
        id: 'stc.unit-record',
        partner: 'stc',
        direction: 'outbound',
        transport: 'rest',
        version: 'v1',
        status: 'current',
        eventTypes: ['unit.registered', 'unit.updated', 'unit.corrected', 'installation.recorded', 'installation.updated', 'installation.corrected'],
        entities: ['unit', 'installation'],
        endpoint: 'POST {STC}/stc/v1/units',
        auth: 'Bearer partner API key',
        idempotency: 'Idempotency-Key header = `<entityId>:<version>`',
        retry: outboundRetry,
        deadLetter: outboundDlq,
        reconciliation: 'GET {STC}/stc/v1/snapshot?interface=stc.unit-record compared with current units.',
        schema: StcUnitRecordV1,
        description: 'Units eligible for certificate claims, with the NMI they are installed at.',
    },
    {
        id: 'stc.claim-recorded',
        partner: 'stc',
        direction: 'inbound',
        transport: 'file',
        version: 'v1',
        status: 'current',
        eventTypes: ['stc.claim.recorded'],
        entities: ['unit'],
        endpoint: 'CSV drop pulled by the worker from {STC}/stc/v1/files; also accepted at POST {REGIGOV}/exchange/inbox/stc/v1/events and POST {REGIGOV}/exchange/inbox/stc/v1/files',
        auth: 'X-Partner-Key header',
        idempotency: 'Each CSV row carries messageId and idempotencyKey; a file is ingested once by name.',
        retry: 'A file that fails to download is retried on the next poll; row failures are dead-lettered individually.',
        deadLetter: inboundDlq,
        reconciliation: 'Claims are partner facts on the unit and are reported in the STC reconciliation run.',
        schema: StcClaimRecordedV1,
        description: `Daily CSV of certificate claims recorded against registered units. Columns: ${STC_CSV_COLUMNS.join(', ')}.`,
    },
    {
        id: 'network.installation-record',
        partner: 'network',
        direction: 'outbound',
        transport: 'rest',
        version: 'v1',
        status: 'current',
        eventTypes: ['installation.recorded', 'installation.updated', 'installation.corrected'],
        entities: ['installation'],
        endpoint: 'POST {NETWORK}/network/v1/installations',
        auth: 'Bearer partner API key',
        idempotency: 'Idempotency-Key header = `<entityId>:<version>`',
        retry: outboundRetry,
        deadLetter: outboundDlq,
        reconciliation: 'GET {NETWORK}/network/v1/snapshot compared with active installations. The optional `location` is not compared.',
        schema: NetworkInstallationRecordV1,
        description: 'Installations commissioned or decommissioned at connection points on the operator’s network. `location` (lat, lon, accuracy) is present when RegiGov has geocoded the installation.',
    },
    {
        id: 'network.commissioning-confirmed',
        partner: 'network',
        direction: 'inbound',
        transport: 'event',
        version: 'v1',
        status: 'current',
        eventTypes: ['network.commissioning.confirmed'],
        entities: ['installation'],
        endpoint: 'POST {REGIGOV}/exchange/inbox/network/v1/events',
        auth: 'X-Partner-Key header',
        idempotency: 'Envelope idempotencyKey.',
        retry: inboundRetry,
        deadLetter: inboundDlq,
        reconciliation: 'Network reference and export limit are partner facts on the installation.',
        schema: NetworkCommissioningConfirmedV1,
        description: 'Event: the network operator confirms commissioning and the export limit for an installation.',
    },
];
/** The inbound schema for a partner/version/event, or null when the combination is not published. */
export function outboundSchema(partner, version, eventType) {
    const def = interfaces.find((i) => i.partner === partner && i.direction === 'outbound' && i.version === version && i.eventTypes.includes(eventType) && i.status !== 'retired');
    return def?.schema ?? null;
}
export function inboundSchema(partner, version, eventType) {
    const def = interfaces.find((i) => i.partner === partner && i.direction === 'inbound' && i.version === version && i.eventTypes.includes(eventType) && i.status !== 'retired');
    return def?.schema ?? null;
}
// ---- API shapes (CER workbench) ------------------------------------------------
export const InterfaceSummary = z.object({
    id: z.string(),
    partner: Partner,
    direction: z.enum(['outbound', 'inbound']),
    transport: Transport,
    version: SchemaVersion,
    status: z.enum(['current', 'supported', 'retired']),
    eventTypes: z.array(z.string()),
    entities: z.array(z.enum(['model', 'unit', 'installation'])),
    endpoint: z.string(),
    auth: z.string(),
    idempotency: z.string(),
    retry: z.string(),
    deadLetter: z.string(),
    reconciliation: z.string(),
    description: z.string(),
    fields: z.array(z.string()),
});
export const InterfaceList = z.object({ items: z.array(InterfaceSummary) });
export const OutboxStatus = z.enum(['pending', 'publishing', 'delivered', 'failed', 'dead']);
export const Delivery = z.object({
    id: z.number().int(),
    attempt: z.number().int(),
    attemptedAt: z.string(),
    outcome: z.enum(['success', 'failure']),
    statusCode: z.number().int().nullable(),
    response: z.string().nullable(),
});
export const OutboxMessage = z.object({
    id: z.number().int(),
    messageId: z.string(),
    partner: Partner,
    interfaceId: z.string(),
    schemaVersion: SchemaVersion,
    eventType: z.string(),
    entityKind: z.enum(['model', 'unit', 'installation']),
    entityId: z.string(),
    entityVersion: z.number().int(),
    idempotencyKey: z.string(),
    status: OutboxStatus,
    attempts: z.number().int(),
    nextAttemptAt: z.string().nullable(),
    lastError: z.string().nullable(),
    effectiveAt: z.string(),
    createdAt: z.string(),
    deliveredAt: z.string().nullable(),
    correlationId: z.string().nullable(),
    payload: z.unknown(),
    deliveries: z.array(Delivery).optional(),
});
export const OutboxList = z.object({ items: z.array(OutboxMessage) });
export const OutboxQuery = z.object({
    partner: Partner.optional(),
    status: OutboxStatus.optional(),
    entityId: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const InboxDisposition = z.enum(['applied', 'late', 'duplicate', 'replayed', 'malformed', 'failed']);
export const InboxMessage = z.object({
    id: z.number().int(),
    messageId: z.string(),
    partner: Partner,
    interfaceId: z.string().nullable(),
    schemaVersion: z.string(),
    eventType: z.string(),
    transport: Transport,
    sourceReference: z.string().nullable(),
    idempotencyKey: z.string(),
    producerTimestamp: z.string().nullable(),
    effectiveDate: z.string().nullable(),
    receivedAt: z.string(),
    disposition: InboxDisposition,
    entityKind: z.enum(['model', 'unit', 'installation']).nullable(),
    entityId: z.string().nullable(),
    detail: z.record(z.unknown()),
    replayOf: z.number().int().nullable(),
    correlationId: z.string().nullable(),
    payload: z.unknown(),
});
export const InboxList = z.object({ items: z.array(InboxMessage) });
export const InboxQuery = z.object({
    partner: Partner.optional(),
    disposition: InboxDisposition.optional(),
    entityId: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
/** Result returned to the producer for an inbound message. */
export const InboxReceipt = z.object({
    inboxId: z.number().int(),
    messageId: z.string(),
    disposition: InboxDisposition,
    entityId: z.string().nullable(),
    detail: z.record(z.unknown()),
});
export const FileDrop = z.object({
    fileName: z.string().min(1).max(200),
    content: z.string().min(1).max(5_000_000),
});
export const FileReceipt = z.object({
    fileName: z.string(),
    rows: z.number().int(),
    receipts: z.array(InboxReceipt),
});
export const DeadLetterStatus = z.enum(['open', 'replayed', 'discarded']);
export const DeadLetter = z.object({
    id: z.number().int(),
    direction: z.enum(['outbound', 'inbound']),
    partner: Partner,
    interfaceId: z.string().nullable(),
    schemaVersion: z.string(),
    eventType: z.string(),
    inboxId: z.number().int().nullable(),
    outboxId: z.number().int().nullable(),
    reason: z.string(),
    errors: z.array(z.object({ field: z.string().optional(), code: z.string(), message: z.string() })),
    status: DeadLetterStatus,
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
    resolvedBy: z.string().nullable(),
    resolvedByName: z.string().nullable(),
    resolutionNote: z.string().nullable(),
    replayInboxId: z.number().int().nullable(),
    payload: z.unknown(),
});
export const DeadLetterList = z.object({ items: z.array(DeadLetter) });
export const DeadLetterQuery = z.object({
    partner: Partner.optional(),
    status: DeadLetterStatus.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
/** Replay a dead letter, optionally with a corrected payload (the envelope fields stay as originally received). */
export const DeadLetterReplay = z.object({
    payload: z.unknown().optional(),
    note: z.string().max(1000).optional(),
}).strict();
export const DeadLetterDiscard = z.object({ note: z.string().min(3).max(1000) }).strict();
export const ReconciliationStatus = z.enum(['running', 'completed', 'failed']);
export const DifferenceDisposition = z.enum(['missing_here', 'missing_there', 'mismatch']);
export const ReviewStatus = z.enum(['unreviewed', 'accepted', 'actioned', 'dismissed']);
export const ReconciliationDifference = z.object({
    id: z.number().int(),
    runId: z.number().int(),
    entityKind: z.enum(['model', 'unit', 'installation']),
    entityId: z.string(),
    disposition: DifferenceDisposition,
    fields: z.array(z.string()),
    registerValue: z.unknown(),
    partnerValue: z.unknown(),
    reviewStatus: ReviewStatus,
    reviewNote: z.string().nullable(),
    reviewedBy: z.string().nullable(),
    reviewedByName: z.string().nullable(),
    reviewedAt: z.string().nullable(),
});
export const ReconciliationRun = z.object({
    id: z.number().int(),
    partner: Partner,
    interfaceId: z.string(),
    status: ReconciliationStatus,
    triggeredBy: z.string(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    registerCount: z.number().int(),
    partnerCount: z.number().int(),
    matched: z.number().int(),
    differences: z.number().int(),
    error: z.string().nullable(),
});
/** A run's differences are read a page at a time, in id order; `next` is the `after` for the following page. */
export const DifferenceQuery = z.object({
    review: z.enum(['all', 'unreviewed']).default('all'),
    after: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const ReconciliationDifferencePage = z.object({ items: z.array(ReconciliationDifference), next: z.number().int().nullable() });
export const ReconciliationRunList = z.object({ items: z.array(ReconciliationRun) });
export const ReconciliationRequest = z.object({ partner: Partner }).strict();
export const DifferenceReview = z.object({ reviewStatus: ReviewStatus.exclude(['unreviewed']), note: z.string().max(1000).optional() }).strict();
export const ExchangeSummary = z.object({
    outbox: z.record(OutboxStatus, z.number().int()),
    inbox: z.record(InboxDisposition, z.number().int()),
    deadLetters: z.record(DeadLetterStatus, z.number().int()),
    lastReconciliation: z.record(Partner, ReconciliationRun.nullable()),
});
//# sourceMappingURL=exchange.js.map